// 隣の島のアンロック（v0.7.1）。インクリメンタル進行の純ロジック。
//
// ホーム島（index 0）の庭で稼いだチップを投資して、隣の島（拡張島）を開く。拡張島には
// カフェも入口も無い「眺めて育てる庭」で、客は来ないが、庭デコの属性は来店レートに
// 全島合算で効く（totalGardenAttributes）＝庭を広げると本店の客が増える。
//
// 序盤は庭デコで稼ぎ、中盤以降の投資先として島を開く。費用は逓増（次の島ほど高い）が、
// 最終島まで現実的に到達しうる範囲に収める。すべて決定的な純関数（乱数なし）。
//
// この層は描画・DOM・乱数に触れない純データ＋純関数。

import type { GameState, Island } from './state';
import { MAX_ISLANDS } from './state';
import { createEmptyGarden } from './garden';

/**
 * 各島をアンロックするのに必要なチップ費用（v0.7.1）。逓増カーブ。
 *
 * index は「これからアンロックする島の index」（= 現在の unlockedIslands）。
 *  - island1（2 つ目・春の別館）= 6,000
 *  - island2（3 つ目・夏の別館）= 28,000
 *  - island3（4 つ目・秋の別館）= 90,000
 *  - island4（5 つ目・冬の別館）= 230,000
 *
 * v0.8.1 バランス改修で引き上げ（旧 1500/6000/18000/45000）。各島は季節の別館として
 * その季節の常連を呼ぶ＝収入の乗算になるので、逓増する費用に見合う投資価値がある
 * （島・スペシャル・席・評判の乗算で 20 時間の背骨を作る、`scripts/balance` で検証）。
 */
const UNLOCK_COSTS: readonly number[] = [0, 6000, 28000, 90000, 230000];

/**
 * 次にアンロックする島（nextIslandIndex = 現在の unlockedIslands）の費用を返す純関数。
 * 上限（MAX_ISLANDS）に達している・範囲外は Infinity（アンロック不可）。
 */
export function islandUnlockCost(nextIslandIndex: number): number {
  if (nextIslandIndex < 1 || nextIslandIndex >= MAX_ISLANDS) return Infinity;
  return UNLOCK_COSTS[nextIslandIndex] ?? Infinity;
}

/** 空の拡張島を作る（カフェ・入口なし＝ensureEntrance しない素の庭）。 */
export function createExtensionIsland(): Island {
  return { garden: createEmptyGarden(), plantings: {} };
}

/** 次の島をアンロックできるか（上限未満かつチップ十分）。 */
export function canUnlockNextIsland(state: GameState): boolean {
  if (state.unlockedIslands >= MAX_ISLANDS) return false;
  return state.chips >= islandUnlockCost(state.unlockedIslands);
}

/**
 * 次の島をアンロックする純関数（v0.7.1）。
 *
 * チップ十分かつ unlockedIslands < MAX_ISLANDS なら、費用ぶん chips を減らし、
 * unlockedIslands を +1、islands に空の拡張島（カフェ・入口なし）を push した新 state を返す。
 * 不足・上限超過は state をそのまま返す（拒否＝同一参照）。入力は破壊しない。
 */
export function unlockNextIsland(state: GameState): GameState {
  if (state.unlockedIslands >= MAX_ISLANDS) return state; // 上限
  const cost = islandUnlockCost(state.unlockedIslands);
  if (!Number.isFinite(cost) || state.chips < cost) return state; // 不足

  const islands = state.islands.slice();
  islands.push(createExtensionIsland());
  return {
    ...state,
    chips: state.chips - cost,
    islands,
    unlockedIslands: state.unlockedIslands + 1,
  };
}
